import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ErrorHandler,
  Injector,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AuthenticationLoader } from '@pflegix/authentication';
import { AuthorizationModule } from '@pflegix/authorization';
import { ConfigurationLoader } from '@pflegix/config';
import { I18nLoader, I18nModule, I18nService } from '@pflegix/i18n';
import { IconLoader, ToastModule } from '@pflegix/ui';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { routes } from './app/app.routes';
import { GlobalErrorHandler } from './app/core/global-error-handler.class';
import { PflegixTheme } from './app/layout/theme';
import { authenticationInterceptor } from './app/shared/interceptor/authentication.interceptor';
import { unauthenticatedInterceptor } from './app/shared/interceptor/unauthenticated.interceptor';
import AppComponent from './app/view/root/app.component';

/**
 * Loads the application by executing the necessary loaders.
 *
 * @param injector - The injector used for dependency injection.
 * @returns A function that returns a promise that resolves when the application is loaded.
 */
function AppLoader(injector: Injector): () => Promise<void> {
  return async () => {
    await ConfigurationLoader(injector)();
    await I18nLoader(injector)();

    const i18nService = injector.get(I18nService);

    injector.get(PrimeNG).setTranslation(i18nService.getResourceBundle(i18nService.language, 'primeng'));

    await AuthenticationLoader(injector)();
    IconLoader(injector)();
  };
}

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(withInterceptors([authenticationInterceptor, unauthenticatedInterceptor])),
    ...(isDevMode() ? [provideStoreDevtools()] : []),
    provideAnimations(),
    providePrimeNG({
      theme: PflegixTheme,
    }),
    provideAppInitializer(() => {
      const initializerFn = AppLoader(inject(Injector));

      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useValue: 'de',
      // deps: [Injector],
      // useFactory: I18nLocalIdLoader,
    },
    provideRouter(routes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    importProvidersFrom(ToastModule.forRoot(), I18nModule.forRoot(), AuthorizationModule.forRoot()),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
  ],
});
